import React, { useContext, useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useGlobalPref } from '../../../hooks/useGlobalPref';
import { useLocalPref } from '../../../hooks/useLocalPref';
import { useNavigate } from '../../../hooks/useNavigate';
import { SvgArrowLeft } from '../../../icons/v1';
import { SvgNavigationMenu } from '../../../icons/v2';
import { useResponsive } from '../../../ResponsiveProvider';
import { theme } from '../../../style';
import { AccountSyncCheck } from '../../accounts/AccountSyncCheck';
import { BankSyncStatus } from '../../BankSyncStatus';
import { MonthCountSelector } from '../../budget/MonthCountSelector';
import { Notifications } from '../../Notifications';
import { useSidebar } from '../../sidebar/SidebarProvider';
import {
  SWITCH_BUDGET_MESSAGE_TYPE,
  Titlebar,
  TitlebarContext,
} from '../../Titlebar';
import { Button, ButtonWithLoading } from '../Button';
import { Link } from '../Link';
import { Paragraph } from '../Paragraph';
import { Popover } from '../Popover';
import { Text } from '../Text';
import { View } from '../View';


import { ProfileDropdown } from './ProfileDropdown';
import UpgradeNowButton from './UpgradeNowButton';


type HeaderProps = {
  pageTitle: string;
};

export function Header({ pageTitle }: HeaderProps) {
  const sidebar = useSidebar();
  const location = useLocation();
  const [floatingSidebar] = useGlobalPref('floatingSidebar');
  const navigate = useNavigate();
  const { isNarrowWidth } = useResponsive();

  return (
    <View
      style={{
        flexDirection: 'row',
        backgroundColor: theme.headerBackground,
        padding: '12px 20px',
        justifyContent: 'space-between',
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {!isNarrowWidth && (floatingSidebar || sidebar.alwaysFloats) && (
          <Button
            type="bare"
            style={{ marginRight: 8 }}
            onPointerEnter={e => {
              if (e.pointerType === 'mouse') {
                sidebar.setHidden(false);
              }
            }}
            onPointerLeave={e => {
              if (e.pointerType === 'mouse') {
                sidebar.setHidden(true);
              }
            }}
            onPointerUp={e => {
              if (e.pointerType !== 'mouse') {
                sidebar.setHidden(!sidebar.hidden);
              }
            }}
          >
            <SvgNavigationMenu
              className="menu"
              style={{ width: 15, height: 15, color: theme.pageText, left: 0 }}
            />
          </Button>
        )}
        {isNarrowWidth && (
          <Link to="/dashboard" style={{ height: '25px' }}>
            <img
              src="/images/gs-logo.png"
              height="100%"
              alt="good steward logo"
            />
          </Link>
        )}
        {!isNarrowWidth && <h1 style={{ margin: 0 }}>{pageTitle}</h1>}

        {!isNarrowWidth && (
          <View style={{ marginLeft: 10 }}>
            <Routes>
              <Route
                path="/accounts"
                element={
                  location.state?.goBack ? (
                    <Button type="bare" onClick={() => navigate(-1)}>
                      <SvgArrowLeft
                        width={10}
                        height={10}
                        style={{ marginRight: 5, color: 'currentColor' }}
                      />{' '}
                      Back
                    </Button>
                  ) : null
                }
              />
              <Route path="/accounts/:id" element={<AccountSyncCheck />} />
              <Route path="/budget" element={<BudgetTitlebar />} />
              <Route path="*" element={null} />
            </Routes>
          </View>
        )}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Titlebar />
        <Notifications />
        <BankSyncStatus />
        <ProfileDropdown />
      </View>
    </View>
  );
}

function BudgetTitlebar() {
  const [maxMonths, setMaxMonthsPref] = useGlobalPref('maxMonths');
  const [budgetType] = useLocalPref('budgetType');
  const { sendEvent } = useContext(TitlebarContext);

  const [loading, setLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const triggerRef = useRef(null);

  const reportBudgetEnabled = useFeatureFlag('reportBudget');

  function onSwitchType() {
    setLoading(true);
    if (!loading) {
      const newBudgetType = budgetType === 'rollover' ? 'report' : 'rollover';
      sendEvent({
        type: SWITCH_BUDGET_MESSAGE_TYPE,
        payload: {
          newBudgetType,
        },
      });
    }
  }

  useEffect(() => {
    setLoading(false);
  }, [budgetType]);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <MonthCountSelector
        maxMonths={maxMonths || 1}
        onChange={value => setMaxMonthsPref(value)}
      />
      {reportBudgetEnabled && (
        <View style={{ marginLeft: -5 }}>
          {/*<ButtonWithLoading*/}
          {/*  ref={triggerRef}*/}
          {/*  type="bare"*/}
          {/*  loading={loading}*/}
          {/*  style={{*/}
          {/*    alignSelf: 'flex-start',*/}
          {/*    padding: '4px 7px',*/}
          {/*  }}*/}
          {/*  title="Learn more about budgeting"*/}
          {/*  onClick={() => setShowPopover(true)}*/}
          {/*>*/}
          {/*  {budgetType === 'report' ? 'Report budget' : 'Rollover budget'}*/}
          {/*</ButtonWithLoading>*/}

          <Popover
            triggerRef={triggerRef}
            placement="bottom start"
            isOpen={showPopover}
            onOpenChange={() => setShowPopover(false)}
            style={{
              padding: 10,
              maxWidth: 400,
            }}
          >
            <Paragraph>
              You are currently using a{' '}
              <Text style={{ fontWeight: 600 }}>
                {budgetType === 'report' ? 'Report budget' : 'Rollover budget'}.
              </Text>{' '}
              Switching will not lose any data and you can always switch back.
            </Paragraph>
            <Paragraph>
              <ButtonWithLoading
                type="primary"
                loading={loading}
                onClick={onSwitchType}
              >
                Switch to a{' '}
                {budgetType === 'report' ? 'Rollover budget' : 'Report budget'}
              </ButtonWithLoading>
            </Paragraph>
            {/*<Paragraph isLast={true}>*/}
            {/*  <Link*/}
            {/*    variant="external"*/}
            {/*    to="https://actualbudget.org/docs/experimental/report-budget"*/}
            {/*    linkColor="muted"*/}
            {/*  >*/}
            {/*    How do these types of budgeting work?*/}
            {/*  </Link>*/}
            {/*</Paragraph>*/}
          </Popover>
        </View>
      )}
    </View>
  );
}
